import { lazy, Suspense } from 'react';
import type { RouteObject } from 'react-router';
import { Outlet } from 'react-router';

import { DashboardLayout } from 'src/layouts/dashboard';
import { LoadingScreen } from 'src/components/loading-screen';
import { AuthGuard, PermissionBasedGuard } from 'src/auth/guard';
import { FeatureFlagGuard } from 'src/auth/guard/feature-flag-guard';
import ClientProfilePage from 'src/pages/clientProfile/ClientProfilePage';
import { Permission } from 'src/assets/data/permissions';

const Dashboard = lazy(() => import(`../../pages/dashboard/DashboardPage`));
const Customers = lazy(() => import(`../../pages/customers/CustomersPage`));
const CustomerProfile = lazy(() => import(`../../pages/customerProfile/CustomerProfilePage`));
const Payments = lazy(() => import(`../../pages/payments/PaymentsPage`));
const UnassignedEmails = lazy(() => import(`../../pages/unassigned-emails/UnassignedEmailsPage`));
const DirectDebitSubscriptions = lazy(
  () => import(`../../pages/direct-debit-subscriptions/DirectDebitSubscriptions`)
);
const Reports = lazy(() => import(`../../pages/reports/ReportsPage`));
const PaymentMatching = lazy(() => import(`../../pages/payment-matching/PaymentMatchingPage`));
// const Imports = lazy(() => import(`../../pages/imports/ImportsPage`));
// const Failures = lazy(() => import(`../../pages/failures/FailuresPage`));
const Analytics = lazy(() => import(`../../pages/analytics/AnalyticsPage`));
const Users = lazy(() => import(`../../pages/users/UsersPage`));
const Clients = lazy(() => import(`../../pages/clients/ClientsPage`));
const Invoices = lazy(() => import(`../../pages/invoices/InvoicesPage`));
const InvoiceOverview = lazy(() => import(`../../pages/invoiceOverview/InvoiceOverview`));
// const Profile = lazy(() => import(`../../pages/profile/ProfilePage`));
// const Security = lazy(() => import(`../../pages/security/SecurityPage`));
// const Settings = lazy(() => import(`../../pages/settings/SettingsPage`));
const WebhookSubscriptions = lazy(
  () => import(`../../pages/webhook-subscriptions/WebhookSubscriptionsPage`)
);
const WebhookEvents = lazy(() => import(`../../pages/webhook-events/WebhookEventsPage`));
const ServiceLine = lazy(() => import(`../../pages/serviceLine/ServiceLinePage`));
const ServiceLineOverview = lazy(
  () => import(`../../pages/serviceLineOverview/ServiceLineOverview`)
);
const OIDCClient = lazy(() => import(`../../pages/api-keys/OIDCClientPage`));

const dashboardLayout = () => (
  <DashboardLayout>
    <Suspense fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

export const dashboardRoutes: RouteObject[] = [
  {
    path: '',
    element: <AuthGuard>{dashboardLayout()}</AuthGuard>,
    children: [
      {
        element: (
          <PermissionBasedGuard
            acceptPermissions={[Permission.ReadDashboard]}
            hasContent
            isDashboard
          >
            <Dashboard />
          </PermissionBasedGuard>
        ),
        index: true,
      },
      {
        path: 'customers',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadCustomer]} hasContent>
            <Customers />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'customer/:id',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadCustomer]} hasContent>
            <CustomerProfile />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'invoices',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadInvoice]} hasContent>
            <Invoices />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'invoice/:id',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadInvoice]} hasContent>
            <InvoiceOverview />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'service-line/:id',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadClient]} hasContent>
            <ServiceLineOverview />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'customer/:customerId/manage-pause',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadCustomer]} hasContent>
            <ServiceLineOverview breadcrumbVariant="customer" />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'payments',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadPayment]} hasContent>
            <Payments />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'communication/unassigned-emails',
        // The `communication` flag has to gate the route, not just the nav entry in
        // `layouts/dashboard/layout.tsx` - otherwise the page stays reachable by URL while
        // the feature is switched off. Same pattern as `reports` and `analytics` below.
        element: (
          // <PermissionBasedGuard acceptPermissions={[Permission.ReadEmail]} hasContent>
          <FeatureFlagGuard flagName="communication">
            <UnassignedEmails />
          </FeatureFlagGuard>
          // </PermissionBasedGuard>
        ),
      },
      {
        path: 'payments/direct-debit-subscriptions',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadPayment]} hasContent>
            <DirectDebitSubscriptions />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'reports',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadReport]} hasContent>
            <FeatureFlagGuard flagName="bop-reports">
              <Reports />
            </FeatureFlagGuard>
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'analytics',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadDashboard]} hasContent>
            <FeatureFlagGuard flagName="bop-analytics-page">
              <Analytics />
            </FeatureFlagGuard>
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'payments/:id/match',
        element: (
          <PermissionBasedGuard
            acceptPermissions={[Permission.ReadPayment, Permission.MatchPayment]}
            hasContent
          >
            <PaymentMatching />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'webhooks',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadWebhooks]} hasContent>
            <WebhookSubscriptions />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'webhooks/history',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadWebhooksHistory]} hasContent>
            <WebhookEvents />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'service-line',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadClient]} hasContent>
            <ServiceLine />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'settings/oidc-client',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadOidcClient]} hasContent>
            <OIDCClient />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'users',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadUser]} hasContent>
            <Users />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'clients',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadClient]} hasContent>
            <Clients />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'client/:id',
        element: (
          <PermissionBasedGuard acceptPermissions={[Permission.ReadClient]} hasContent>
            <ClientProfilePage />
          </PermissionBasedGuard>
        ),
      },

      // { path: 'imports', element: <Imports /> },
      // { path: 'failures', element: <Failures /> },
      // { path: 'analytics', element: <Analytics /> },
      // { path: 'reports', element: <Reports /> },
      // { path: 'profile', element: <Profile /> },
      // { path: 'security', element: <Security /> },
      // { path: 'settings', element: <Settings /> },
    ],
  },
];
